import { useEffect, useState } from "react";

import { Modal } from "./Modal";

interface Shortcut {
	keys: string[];
	description: string;
}

const SHORTCUTS: Shortcut[] = [
	{ keys: ["⌘", "K"], description: "Open command palette" },
	{ keys: ["?"], description: "Show this help" },
	{ keys: ["/"], description: "Focus search in current sidebar" },
	{ keys: ["↑", "↓"], description: "Move selection in sidebar" },
	{ keys: ["Enter"], description: "Open selected item" },
	{ keys: ["Esc"], description: "Close palette / cancel edit" },
	{ keys: ["Double-click"], description: "Rename title (Tasks / Goals / Projects)" },
	{ keys: ["Drag", "→", "Project"], description: "Reassign a task" },
	{ keys: ["Drag", "→", "Project card"], description: "Attach a goal" },
];

/**
 * Global keyboard shortcut cheatsheet. Toggled with `?` (Shift+/).
 * Mounted once at the app root next to the command palette.
 */
export function ShortcutHelp() {
	const [open, setOpen] = useState(false);

	useEffect(() => {
		const handler = (e: KeyboardEvent) => {
			const target = e.target as HTMLElement;
			const isTyping =
				target instanceof HTMLInputElement ||
				target instanceof HTMLTextAreaElement ||
				(target && target.isContentEditable);
			if (e.key === "?" && !isTyping) {
				// A page that renders its own richer shortcuts help claims the
				// "?" key by carrying data-owns-shortcut-help on its root (the
				// wiki does). Yield — otherwise both dialogs open stacked.
				if (document.querySelector("[data-owns-shortcut-help]")) return;
				e.preventDefault();
				setOpen((v) => !v);
			}
			// Escape is handled by the shared Modal while open.
		};
		document.addEventListener("keydown", handler);
		return () => document.removeEventListener("keydown", handler);
	}, [open]);

	return (
		<Modal
			open={open}
			onClose={() => setOpen(false)}
			title="Keyboard Shortcuts"
			maxWidth="min(440px, calc(90vw / var(--ui-scale, 1)))"
		>
			<div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
					{SHORTCUTS.map((s, i) => (
						<div
							key={i}
							style={{
								display: "flex",
								alignItems: "center",
								justifyContent: "space-between",
								gap: 12,
								padding: "6px 0",
								borderBottom:
									i < SHORTCUTS.length - 1 ? "1px solid var(--border)" : "none",
							}}
						>
							<span style={{ fontSize: 13, color: "var(--text-secondary)" }}>
								{s.description}
							</span>
							<div style={{ display: "flex", gap: 4 }}>
								{s.keys.map((k, j) => (
									<kbd
										key={j}
										style={{
											padding: "2px 8px",
											background: "var(--bg-tertiary)",
											border: "1px solid var(--border)",
											borderRadius: 4,
											fontSize: 13,
											fontFamily: "var(--font-mono)",
											color: "var(--text-primary)",
										}}
									>
										{k}
									</kbd>
								))}
							</div>
					</div>
				))}
			</div>
			<div
				style={{
					marginTop: 14,
					fontSize: 12,
					color: "var(--text-muted)",
					textAlign: "center",
				}}
			>
				Press <kbd>?</kbd> to toggle • <kbd>Esc</kbd> to close
			</div>
		</Modal>
	);
}
